<script setup lang="ts">
import { onMounted, ref, reactive } from 'vue'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import QRCode from 'qrcode'
import { useRoute } from 'vue-router';

import config from "@/app.config.json";
import * as service from "@/services/clientesService";
import * as dependentesService from "@/services/clientesDependentesService";
import * as contratosService from "@/services/clientesContratosService";
import { fdata } from "@/utils/funcoes";

// Importe suas imagens
import cartaoFrente from '@/assets/images/cartao-frente.png'
import cartaoVerso from '@/assets/images/cartao-verso.png'

const route = useRoute();
const frenteRef = ref(null)
const versoRef = ref(null)
const pdfUrl = ref('');
const qrCodeUrl = ref('');

var controle = reactive({
  model: {} as service.Cliente,
  dependentes: [] as dependentesService.Dependente[],
  contrato: null as contratosService.Contrato | null,
  status: "",
  erro: ""
});

onMounted(() => {
  console.log('Carregando cartão para ID:', route.params.id);
  if (route.params.id) {
    service.obterPorId(Number(route.params.id))
      .then(async r => {
        controle.model = r;

        const urlValidador = `${config.urlPainel}/validador/${r.clienteId}`;
        qrCodeUrl.value = await QRCode.toDataURL(urlValidador, { width: 200, margin: 1 });

        // Aguardar um pouco para garantir que as imagens carregaram
        setTimeout(() => {
          gerarPdf();
        }, 500);
      })
      .catch(error => {
        controle.erro = `Erro ao carregar: ${error}`;
      });

      dependentesService.obter({clienteId: Number(route.params.id)} as dependentesService.Filtros)
     .then(r => {
        controle.dependentes = r;
        // Aguardar um pouco para garantir que as imagens carregaram
      })
      .catch(error => {
        controle.erro = `Erro ao carregar: ${error}`;
      });

      contratosService.obter({clienteId: Number(route.params.id)} as contratosService.Filtros)
     .then(r => {
        controle.contrato = r[0] || null;
        // Aguardar um pouco para garantir que as imagens carregaram
      })
      .catch(error => {
        controle.erro = `Erro ao carregar: ${error}`;
      });
  }
})

const gerarPdf = async () => {
  if (!frenteRef.value || !versoRef.value) return

  controle.status = "Gerando PDF...";

  try {
    // Tamanho do cartão PVC: 86mm x 54mm
    const CARTAO_LARGURA_MM = 177
    const CARTAO_ALTURA_MM = 110
    
    // Margem superior esquerda
    const MARGEM_X = 0
    const MARGEM_Y = 0
    
    // Captura as imagens com alta resolução
    const [canvasFrente, canvasVerso] = await Promise.all([
      html2canvas(frenteRef.value, { 
        scale: 3,
        useCORS: true, 
        backgroundColor: '#ffffff',
        allowTaint: false,
        logging: false
      }),
      html2canvas(versoRef.value, { 
        scale: 3,
        useCORS: true, 
        backgroundColor: '#ffffff',
        allowTaint: false,
        logging: false
      })
    ])

    const frenteData = canvasFrente.toDataURL('image/PNG', 1.0)
    const versoData = canvasVerso.toDataURL('image/PNG', 1.0)

    // Criar PDF A4
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [177, 110] // Tamanho do cartão PVC em mm
    })

    // Página 1 - Frente do cartão no canto superior esquerdo
    pdf.addImage(
      frenteData, 
      'PNG', 
      MARGEM_X, 
      MARGEM_Y, 
      CARTAO_LARGURA_MM, 
      CARTAO_ALTURA_MM,
      '',
      'FAST'
    )

    // Adicionar bordas de corte do cartão (linhas guia)
    pdf.setDrawColor(200, 200, 200)
    pdf.setLineWidth(0.1)
    pdf.rect(MARGEM_X, MARGEM_Y, CARTAO_LARGURA_MM, CARTAO_ALTURA_MM)

    // Nova página para o verso
    pdf.addPage()
    
    // Página 2 - Verso do cartão no canto superior esquerdo
    pdf.addImage(
      versoData, 
      'PNG', 
      MARGEM_X, 
      MARGEM_Y, 
      CARTAO_LARGURA_MM, 
      CARTAO_ALTURA_MM,
      '',
      'FAST'
    )

    // Adicionar bordas de corte do cartão (linhas guia)
    pdf.setDrawColor(200, 200, 200)
    pdf.setLineWidth(0.1)
    pdf.rect(MARGEM_X, MARGEM_Y, CARTAO_LARGURA_MM, CARTAO_ALTURA_MM)

    // Adicionar metadados
    pdf.setProperties({
      title: `Cartão PVC - ${controle.model.nome}`,
      subject: 'Cartão de Identificação',
      creator: 'Sistema PrevVida',
      keywords: 'cartão, pvc, identificação'
    })

    // Gerar Blob e exibir automaticamente
    const blob = pdf.output('blob')
    const url = URL.createObjectURL(blob)
    pdfUrl.value = url

    controle.status = "PDF gerado com sucesso!"

  } catch (error) {
    controle.erro = `Erro ao gerar PDF: ${error}`
    console.error('Erro detalhado:', error)
  }
}
</script>

<template>
  <div class="container-fluid p-4">

    <!-- Elementos ocultos para captura -->
    <div style="position: absolute; left: -9999px; top: -9999px;">
      
      <div ref="frenteRef" class="cartao-container" style="width: 320px; height: 202px; background: white;">
        <img
          :src="cartaoFrente"
          alt="Frente do cartão PVC"
          style="width: 100%; height: 100%; object-fit: cover; display: block;"
        />
        <!-- Overlay com dados do cliente -->
        <div class="cartao-dados"  style="position: absolute; top: 137px; left: 28px; color: white; font-family: Arial, sans-serif;">
          <div style="font-size: 13px; line-height: 12px; font-weight: bold; margin-bottom: 0px; height: 32px; width: 280px;">
            {{ controle.model.nome }}
          </div>
          <div style="position:absolute; font-size: 12px; font-weight: bold; left: 2px; top: 30px;">
             {{ controle.model.cpf }}
          </div>
          <div style="position:absolute; font-size: 12px; font-weight: bold; left: 95px; top: 30px;">
             {{ controle.model.clienteId }}
          </div>
           <!-- <div style="position:absolute; font-size: 11px; left: 216px; top: 19px;">
             {{ fdata(controle.contrato?.dataFim) }}
          </div> -->
        </div>
      </div>

      <div ref="versoRef"
        class="cartao-container"
        style="width: 320px; height: 202px; background: white;">
        <img :src="cartaoVerso"  alt="Verso do cartão PVC" style="width: 100%; height: 100%; object-fit: cover; display: block;"/>
         
        <!-- dependentes -->
        <div class="cartao-dependentes" style="position: absolute; top: 35px; left: 190px; color: white; text-align: left; font-family: Arial, sans-serif;"        >
          
          <div style="font-size: 11px; font-weight: bold;  margin-bottom: 7px; line-height: 10px;" v-for="(dep, idx) in controle.dependentes" :key="idx">
            {{ dep.nome }} 
          </div>
          
        </div>

        <!-- QR Code de validação -->
        <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR Code de validação"
          style="position: absolute; top: 50%; left: 15px; transform: translateY(calc(-50% - 10px)); width: 80px; height: 80px;" />
      </div>
    </div>

    <!-- Conteúdo principal -->
    <div class="row justify-content-center">
      <div class="col-12 text-center">
        
        <!-- Status -->
        <div v-if="controle.status && !pdfUrl" class="alert alert-info mb-3">
          <i class="fas fa-spinner fa-spin me-2"></i>{{ controle.status }}
        </div>
        
        <div v-if="controle.erro" class="alert alert-danger mb-3">
          <i class="fas fa-exclamation-triangle me-2"></i>{{ controle.erro }}
        </div>

        <!-- PDF Viewer -->
        <div v-if="pdfUrl" class="text-center">
          <div class="alert alert-success mb-3">
            <i class="fas fa-check-circle me-2"></i>
            Cartão gerado com sucesso! Pronto para impressão.
          </div>
          
          <div class="border rounded p-2" style="max-width: 100%; margin: 0 auto;">
            <iframe 
              :src="pdfUrl" 
              style="width: 100%; height: 80vh; border: none; border-radius: 8px;"
              title="Cartão PDF - Pronto para Impressão"
            />
          </div>
          
          <div class="mt-3">
            <small class="text-muted">
              <i class="fas fa-info-circle me-1"></i>
              Cartão no tamanho 86mm x 54mm com linhas de corte para facilitar o acabamento
            </small>
          </div>
        </div>

        <!-- Loading se ainda não carregou -->
        <div v-if="!pdfUrl && !controle.erro" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Carregando...</span>
          </div>
          <p class="mt-3">Preparando cartão para impressão...</p>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.cartao-container {
  position: relative;
  display: inline-block;
}

.cartao-dados {
  
}

/* Garantir que as imagens sejam renderizadas corretamente */
img {
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}
</style>